import { useEffect, useState } from "react";
import { faMinus, faPlus } from "@fortawesome/pro-regular-svg-icons";
import cx from "classnames";

import { useDebounceCallback } from "common/hooks";
import { clamp } from "common/utils/numbers";
import Icon from "common/components/Icon";

import { Optional } from "../../../../common/utils/types";

import styles from "../../styles/QuantityInput.module.less";

type QuantityInputProps = {
  onChange: (quantity: number) => void;
  value: number;
  className?: string;
  min?: number;
  max?: Optional<number>;
  disabled?: boolean;
  onRemove?: () => void;
  size?: "s" | "m";
};

const QuantityInput = ({
  onChange,
  value,
  className,
  min = 0,
  max: propMax,
  disabled,
  onRemove,
  size = "m",
}: QuantityInputProps) => {
  const max = propMax ?? Number.MAX_SAFE_INTEGER;
  const [lastValue, setLastValue] = useState(value);
  const [input, setInput] = useState<number | "">(value);

  useEffect(() => {
    setInput(value);
  }, [value]);

  const onFocus = () => {
    setInput("");
    setLastValue(value);
  };

  const onBlur = () => {
    const nextValue = clamp(input || lastValue, min, max);
    onChange(nextValue);
    setInput(nextValue);
  };

  const onDecrement = () => onChange(value - 1);
  const onIncrement = () => onChange(value + 1);

  const onKeyDown = (event: React.KeyboardEvent) => {
    // Prevent any math symbols from sneaking through for a better UX
    const symbols = ["-", "+", "e", "."];

    if (symbols.includes(event.key)) {
      event.preventDefault();
    }
  };

  const shouldRemove = value === 1 && onRemove;
  const isDecrementDisabled = (value <= min || disabled) && !shouldRemove;
  const isIncrementDisabled = value >= max || disabled;

  return (
    <div className={cx(styles.container, styles[`size-${size}`], className)}>
      <button
        className={styles.action}
        type="button"
        disabled={isDecrementDisabled}
        onClick={shouldRemove ? onRemove : onDecrement}
      >
        <Icon icon={faMinus} />
      </button>
      <input
        autoComplete="off"
        type="number"
        // 9 digit keypad instead of any number/symbol on ios
        inputMode="numeric"
        pattern="[0-9]+"
        step="1"
        value={input}
        onFocus={onFocus}
        onChange={(event) => setInput(parseInt(event.target.value, 10) || "")}
        onKeyDown={onKeyDown}
        onBlur={onBlur}
        className={styles.input}
      />
      <button
        className={styles.action}
        type="button"
        disabled={isIncrementDisabled}
        onClick={onIncrement}
      >
        <Icon icon={faPlus} />
      </button>
    </div>
  );
};

const QuantityInputDebounce = ({
  value,
  onChange,
  debounce,
  ...other
}: QuantityInputProps & { debounce?: number }) => {
  const [input, setInput] = useState(value);

  const debounceOnChange = useDebounceCallback((val: number) => onChange(val), debounce);

  const handleChange = (val: number) => {
    if (val === input) {
      return;
    }

    debounceOnChange(val);
    setInput(val);
  };

  // Keep the internal input in sync with the external value
  useEffect(() => {
    setInput(value);
  }, [value]);

  return <QuantityInput value={input} onChange={handleChange} {...other} />;
};

export default Object.assign(QuantityInput, { Debounce: QuantityInputDebounce });
